"use client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import React, { useEffect } from "react";
import { useCarousel } from "@/hooks/utility/useCarousel";
import PaginationStyleSelector from "@/components/shared/carousel/pagination/Pagination";
import { category } from "@/components/shared/carousel/pagination/type";
import AutoHeight from "embla-carousel-auto-height";
import Autoplay from "embla-carousel-autoplay";

interface BannerCarouselProps {
  children: React.ReactNode;
  className?: string;
  itemClassName?: string;
  media?: { type: "image" | "video"; src: string; thumbnail: string }[];
  overlay?: React.ReactNode;
  paginationDotsStyle?: string;
  navigationType?: "image" | "dots" | "number";
  paginationContainerStyle?: string;
  leftButtonStyle?: string;
  rightButtonStyle?: string;
  autoHeight?: boolean;
  showPagination?: boolean;
  maxItems?: number;
  initialIndex?: number;
  orientation?: "horizontal" | "vertical";
  align?: "start" | "center" | "end";
  autoplay?: boolean;
  autoplayDelay?: number;
}

const BannerCarouselWrapper = ({
  navigationType = "dots",
  children,
  className,
  itemClassName,
  leftButtonStyle,
  rightButtonStyle,
  media,
  overlay,
  paginationContainerStyle,
  autoHeight,
  showPagination = true,
  maxItems = 4,
  initialIndex,
  orientation = "horizontal",
  align = "center",
  autoplay = false,
  autoplayDelay = 4000,
}: BannerCarouselProps) => {
  const { carouselApi, setCarouselApi, currentIndex } = useCarousel();

  const plugins = [
    ...(autoHeight ? [AutoHeight()] : []),
    ...(autoplay
      ? [Autoplay({ delay: autoplayDelay, stopOnInteraction: false })]
      : []),
  ];

  const allChildren = Array.isArray(children) ? children : [children];
  const displayedChildren =
    allChildren.length > maxItems
      ? allChildren.slice(0, maxItems)
      : allChildren; // thumbnails for pagination limited to maxItems
  const thumbnails = media
    ? (media.length > maxItems ? media.slice(0, maxItems) : media).map(
        (item) => item.thumbnail || item.src
      )
    : [];

  useEffect(() => {
    if (initialIndex) {
      carouselApi?.scrollTo(initialIndex);
    }
  }, [initialIndex, carouselApi]);

  return (
    <Carousel
      plugins={plugins}
      opts={{
        loop: true,
        axis: orientation === "vertical" ? "y" : "x",
        startIndex: initialIndex || 0,
        align,
      }}
      orientation={orientation}
      setApi={setCarouselApi}
      className={cn("group relative h-full w-full", className)}>
      {overlay}

      <CarouselContent
        className={cn("h-full", orientation === "vertical" && "h-dvh")}>
        {displayedChildren.map((child, index) => (
          <CarouselItem
            key={index}
            className={cn("h-full flex-shrink-0", itemClassName)}>
            {child}
          </CarouselItem>
        ))}
      </CarouselContent>
      {carouselApi?.canScrollNext && displayedChildren.length > 1 && (
        <CarouselNext
          className={cn(
            "absolute top-1/2 right-4 cursor-pointer bg-black/40 text-white transition-opacity duration-300 group-hover:opacity-100 md:opacity-0",
            rightButtonStyle
          )}
          onClick={(e) => {
            e.preventDefault();
            carouselApi?.scrollNext();
          }}
        />
      )}
      {carouselApi?.canScrollPrev && displayedChildren.length > 1 && (
        <CarouselPrevious
          className={cn(
            "absolute top-1/2 left-4 cursor-pointer bg-black/40 text-white transition-opacity duration-300 group-hover:opacity-100 md:opacity-0",
            leftButtonStyle
          )}
          onClick={(e) => {
            e.preventDefault();
            carouselApi?.scrollPrev();
          }}
        />
      )}
      {showPagination && (
        <div className="absolute bottom-0 flex w-full transform gap-2">
          <div
            className={cn(
              "flex w-full items-center justify-center gap-1",
              paginationContainerStyle
            )}>
            {category(navigationType) === "multi-indicator" ? (
              displayedChildren.map((_, index) => (
                <PaginationStyleSelector
                  key={index}
                  navigationType={navigationType}
                  currentIndex={currentIndex}
                  carouselApi={carouselApi}
                  imgSrc={thumbnails}
                  index={index}
                />
              ))
            ) : (
              <PaginationStyleSelector
                index={0}
                navigationType={navigationType}
                currentIndex={currentIndex}
                carouselApi={carouselApi}
                imgSrc={thumbnails}
              />
            )}
          </div>
        </div>
      )}
    </Carousel>
  );
};

export default BannerCarouselWrapper;
